import React, { useState } from 'react';
import { NewsStoryProps } from './NewsStory';
import { motion, AnimatePresence } from 'framer-motion';
import { Footer, MotionImage, useSanitizedTranslation } from 'ui';
import { useRouter } from 'next/router';
import { useTimer } from 'react-timer-hook';
import { ShareIcon, LinkIcon, InstagramIcon, LinkedInIcon } from './icons';
import { PressSidebar } from './PressSidebar';
import { getContentFromCollection } from '../utils/mapping';
getContentFromCollection;
import { MappingPageContent, MappingContentBlock } from '../utils/mapping';
import { PageContent } from 'ui';
import { SingleImageBlock } from './blocks';
import { stripHTML } from 'config/utils/stripHTML';

export type PressBlockProps = {
  heading: string;
  body?: string;
  stories: NewsStoryProps[];
};

const variants = {
  initial: (i: number) => ({
    opacity: 0,
    x: -40,
  }),
  animate: (i: number) => ({
    x: 0,
    y: 0,
    opacity: 1,
    transition: { staggerChildren: 0.075, ease: 'easeInOut', duration: 0.5 },
  }),
};

const ShareLinks: React.FC<{ articletitle: string }> = ({ articletitle }) => {
  const linkClass = 'cursor-pointer w-4 hover:scale-110 transition-all group/icon';
  const borderClass = 'w-[1px] h-5 bg-[#848484]';
  const iconClass = 'fill-xumoDarkPlum hover:fill-xumoPlum';

  // Share
  const canUseWebShare = (): boolean => typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const shareNative = async (url: string): Promise<boolean> => {
    if (!canUseWebShare()) return false;

    try {
      await navigator.share({
        url,
        title: articletitle,
      });
      return true;
    } catch {
      return false; // user canceled
    }
  };

  // Copy
  const [showCopied, setShowCopied] = useState(false);

  const copyToClipboard = async (text: string): Promise<void> => {
    if (typeof navigator === 'undefined') return;

    let success = false;

    try {
      await navigator.clipboard.writeText(text);
      success = true;
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        success = true;
      } catch {
        success = false;
      }
    }

    if (success) {
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 1500);
    }
  };

  // LinkedIn
  const shareLinkedIn = (url: string): void => {
    if (typeof window === 'undefined') return;

    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;

    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  // Instagram - won't actually work basically opens Instagram and copies the link to the clipboard
  const shareInstagram = async (url: string): Promise<void> => {
    if (typeof window === 'undefined') return;

    await copyToClipboard(url);

    // Mobile deep link
    window.location.href = 'instagram://app';

    // Desktop / fallback
    setTimeout(() => {
      window.open('https://www.instagram.com/', '_blank');
    }, 400);
  };

  return (
    <div className="mb-2 ml-0.5 flex flex-row items-center justify-start gap-2.5">
      {/* Share */}
      <div className={linkClass} onClick={() => shareNative(window.location.href)}>
        <ShareIcon className={iconClass} />
      </div>
      <div className={borderClass}></div>
      {/* Copy */}
      <div className="flex flex-col items-center justify-center gap-5">
        <div className={linkClass} onClick={() => copyToClipboard(window.location.href)}>
          <LinkIcon className={iconClass} />
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: showCopied ? 1 : 0 }}
          transition={{ ease: 'easeInOut', duration: 0.3 }}
          className="pointer-events-none absolute mb-20 ml-8 rounded-full bg-gray-800 px-4 py-1.5 text-white"
        >
          <p>Copied to clipboard</p>
        </motion.div>
      </div>
      <div className={borderClass}></div>
      {/* Linkedin */}
      <div className={linkClass} onClick={() => shareLinkedIn(window.location.href)}>
        <LinkedInIcon className={iconClass} />
      </div>
      <div className={borderClass}></div>
      {/* Instagram */}
      <div className={linkClass} onClick={() => shareInstagram(window.location.href)}>
        <InstagramIcon className={iconClass} />
      </div>
    </div>
  );
};

const OldPressImage: React.FC<{
  additionalImages: any;
  thumbnail: any;
  hide_thumbnail: boolean;
}> = ({ additionalImages, thumbnail, hide_thumbnail }) => {
  const carouselMode = !!additionalImages.length;
  const carouselImages = [thumbnail, ...additionalImages];

  const [slide, setSlide] = useState(0);

  const fiveSeconds = () => {
    return new Date(new Date().getTime() + 5000);
  };
  const time = new Date();
  const { totalSeconds, seconds, minutes, hours, days, isRunning, start, pause, resume, restart } = useTimer({
    expiryTimestamp: fiveSeconds(),
    autoStart: carouselMode,
    onExpire: () => {
      updateSlide();
    },
  });
  const updateSlide = slideNum => {
    setSlide(slide => (typeof slideNum !== 'undefined' ? slideNum : (slide + 1) % carouselImages.length));
    if (typeof slideNum === 'undefined') {
      setTimeout(() => restart(fiveSeconds(), true), 5);
    } else {
      pause();
    }
  };

  return (
    <>
      {!carouselMode && !hide_thumbnail && (
        <motion.div className={`test relative w-full overflow-hidden rounded`} style={{ aspectRatio: thumbnail.width / thumbnail.height }}>
          <MotionImage
            variants={variants}
            src={thumbnail.src}
            alt={thumbnail.alt ?? ''}
            fill
            className="object-contain object-left"
            sizes={'(max-width: 768px) 100vw, (max-width: 1600px) 60vw, 40vw'}
            priority
          />
        </motion.div>
      )}
      {carouselMode && (
        <>
          <div className="relative mt-8 aspect-video">
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, zIndex: 5 }}
                animate={{ opacity: 1, zIndex: 4 }}
                exit={{ opacity: 0, zIndex: 3, transition: { delay: 2 } }}
                key={'custom' + slide}
                className={`absolute left-0 top-0  aspect-video w-full overflow-hidden rounded`}
              >
                <MotionImage
                  src={carouselImages[slide].src}
                  alt={carouselImages[slide].alt ?? ''}
                  fill
                  className="object-contain object-left"
                  sizes={'(max-width: 768px) 100vw, (max-width: 1600px) 75vw, 55vw'}
                  priority
                />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="my-2 flex items-center justify-center pb-8">
            {carouselImages.map((_, id) => (
              <button
                key={'slide-control' + id}
                aria-label={`Slide ${id + 1}`}
                aria-current={slide === id ? 'true' : 'false'}
                className={`${
                  slide === id ? 'w-12' : 'w-3'
                } relative m-1 inline-block h-3 rounded-full bg-black opacity-60 transition-all hover:bg-[#BB2E76] hover:opacity-100`}
                onClick={() => updateSlide(id)}
              />
            ))}

            <button
              onClick={() => (isRunning ? pause() : resume())}
              aria-label={isRunning ? 'Pause' : 'Play'}
              aria-pressed={isRunning}
              className="ml-8 flex h-4 w-4 items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              {isRunning ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6f h-6 fill-black opacity-60 transition-all hover:fill-[#BB2E76] hover:opacity-100"
                  viewBox="0 0 448 512"
                  aria-hidden="true"
                >
                  <path d="M144 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h96c26.51 0 48-21.49 48-48V80c0-26.51-21.49-48-48-48zm256 0h-96c-26.51 0-48 21.49-48 48v352c0 26.51 21.49 48 48 48h96c26.51 0 48-21.49 48-48V80c0-26.51-21.49-48-48-48z" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 fill-black opacity-60 transition-all hover:fill-[#BB2E76] hover:opacity-100"
                  viewBox="0 0 448 512"
                  fill="#BB2E76"
                  aria-hidden="true"
                >
                  <path d="M424.41 214.66L72.41 3.66C51.82-7.72 24 5.52 24 32v448c0 26.48 27.82 39.72 48.41 28.34l352-211c22.49-13.49 22.49-43.19 0-56.68z" />
                </svg>
              )}
            </button>
          </div>
        </>
      )}
      <div aria-hidden="true" style={{ position: 'absolute', left: -9999, top: -9999 }}>
        {carouselImages.map((image, index) => (
          <MotionImage key={index} src={image.src} alt={''} fill priority={index < 2} sizes={'(max-width: 768px) 100vw, (max-width: 1600px) 75vw, 55vw'} />
        ))}
      </div>
    </>
  );
};

export const PressBlock: React.FC<{
  title: string;
  date: Date;
  description: string;
  content: string;
  thumbnail: any;
  hide_thumbnail: boolean; // part of old posts
  additionalImages: any[]; //part of old posts
  article_content: any;
  after_2026_q1_update: boolean;
  sidebar_similar_content: any;
  sidebar_featured_resource: any;
  sidebar_featured_resource_image: any;
  sidebar_form: string;
  hero_full_width_image: boolean;
  image_description: string;
  footer: any;
}> = ({
  title,
  date: d,
  description,
  content,
  thumbnail,
  hide_thumbnail,
  additionalImages = [],
  after_2026_q1_update,
  sidebar_similar_content,
  sidebar_featured_resource,
  sidebar_featured_resource_image,
  sidebar_form,
  hero_full_width_image,
  article_content,
  image_description,
  footer,
}) => {
  const { t } = useSanitizedTranslation();
  const router = useRouter();
  const date = new Date(d);

  const variants = {
    initial: {
      opacity: 0,
      y: 20,
      transition: { type: 'spring', damping: 65, stiffness: 900, mass: 1 },
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: { staggerChildren: 0.1, type: 'spring', damping: 65, stiffness: 900, mass: 1 },
    },
  };

  const articleContent = (article_content ?? [])
    .map((block: any) => getContentFromCollection(block as MappingPageContent))
    .filter((c): c is MappingContentBlock => !!c);

  // News block and disclaimer will always appear after the article at full width, so if they exist they are pulled out and added to the end
  const afterArticle = articleContent.filter((item: any) => item.component === 'NewsBlock' || item.component === 'Disclaimer');
  const mainContent = articleContent.filter((item: any) => item.component != 'NewsBlock' && item.component != 'Disclaimer');

  // Stripped HTML title
  const strippedTitle = stripHTML(title);

  return (
    <>
      <section className={`flex flex-col items-start justify-evenly gap-12 ${hero_full_width_image ? '' : 'mt-10'} mb-10`}>
        {/* Full Width Image */}
        {hero_full_width_image && after_2026_q1_update && (
          <div className="flex w-full flex-col bg-[#C3C3C3]">
            <SingleImageBlock image={thumbnail} />
            {/* Custom caption for hero */}
            {thumbnail?.alt && (
              <motion.p initial="initial" animate="animate" variants={variants} className="wrapper type-caption-press py-4">
                {thumbnail.alt}
              </motion.p>
            )}
          </div>
        )}
        {/* Content & Sidebar Wrapper */}
        <div className="wrapper flex flex-col items-start justify-evenly gap-12 lg:flex-row xl:gap-28 xl:px-20">
          {/* MAIN CONTENT */}
          <div className="flex h-full w-full flex-col items-start justify-start gap-8 md:gap-10 xl:gap-12">
            {/* Title */}
            <motion.h1
              initial="initial"
              animate="animate"
              variants={variants}
              data-i18n
              className={`type-heading-press`}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            {/* Hero Image */}
            {after_2026_q1_update ? (
              !hero_full_width_image && <SingleImageBlock image={thumbnail} image_description={image_description} />
            ) : (
              <OldPressImage additionalImages={additionalImages} thumbnail={thumbnail} hide_thumbnail={hide_thumbnail} />
            )}
            {/* Description & Share Links */}
            <motion.div initial="initial" animate="animate" variants={variants} className="flex flex-col items-start justify-start gap-4 md:gap-5">
              <motion.div data-i18n variants={variants} className={`type-description-press`} dangerouslySetInnerHTML={{ __html: description }} />
              <time data-i18n dateTime={date.toISOString()} className="type-date-press mb-0.5 mt-3">
                {date.toLocaleDateString(router.locale, { month: 'long', day: 'numeric', year: 'numeric' })}
              </time>
              <ShareLinks articletitle={strippedTitle} />
            </motion.div>
            {/* OLD Content - keeping for old posts */}
            {!after_2026_q1_update && (
              <motion.div
                data-i18n
                variants={variants}
                className={`type-body allow-html text-left ${hide_thumbnail ? 'pt-6' : ''}`}
                dangerouslySetInnerHTML={{ __html: content }}
              />
            )}
            <div className="flex w-full flex-col gap-10 md:gap-14">
              <PageContent page={{ content: mainContent }} />
            </div>
          </div>
          {/* SIDEBAR CONTENT */}
          {(sidebar_similar_content.length > 0 || sidebar_featured_resource || sidebar_form) && (
            <PressSidebar
              sidebar_similar_content={sidebar_similar_content}
              sidebar_featured_resource={sidebar_featured_resource}
              sidebar_featured_resource_image={sidebar_featured_resource_image}
              sidebar_form={sidebar_form}
            />
          )}
        </div>
        {afterArticle.length > 0 && (
          <div className="mt-5 flex h-full w-full flex-col">
            <PageContent page={{ content: afterArticle }} />
          </div>
        )}
      </section>
      <Footer {...footer} />
    </>
  );
};
